import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Form, Button, Card, Row, Col, Alert } from "react-bootstrap";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import ISO6391 from "iso-639-1";
import { Upload, X, Image as ImageIcon, Eye } from "lucide-react";
import { BiLaptop, BiMobile } from "react-icons/bi";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Breadcrumb from "../../components/Common/Bredcrump";
import StreamingContentRenderer from "../../components/Common/StreamingContentRenderer";
import LocalDraftModal from "../../components/EditProductDescription/LocalDraftModal";
import { generateContentWithStreaming, mockGenerateContent, saveFreeFormContentDraft, updateFreeFormContentDraft } from "../../services/freeFormContentService";
import { translateContent } from "../../services/translationService";
import useStreamingContent from "../../hooks/useStreamingContent";
import { uploadCompanyKnowledgeFiles } from "../../utils/Product";
import "./FreeFormContent.css";
import axiosInstance from "../../utils/api";

const EditFreeFormContent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const contentData = location.state?.contentData;
  const [formData, setFormData] = useState({
    title: "",
    prompt: "",
    language: null,
    locationFocus: null,
    role: "",
    goal: "",
    constraints: "",
  });

  const [uploadedData, setUploadedData] = useState({
    tags: "",
    uid: "",
  });

  // Image upload state
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageUrl, setImageUrl] = useState("");
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [imageError, setImageError] = useState("");
  const fileInputRef = useRef(null);

  const [allLocations, setAllLocations] = useState([]);

  // Content state tracking
  const [hasGeneratedContent, setHasGeneratedContent] = useState(false);
  const [savedContentId, setSavedContentId] = useState(null);
  const [isContentSaved, setIsContentSaved] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Draft update modal state
  const [showDraftModal, setShowDraftModal] = useState(false);

  // Translation state
  const [isTranslating, setIsTranslating] = useState(false);
  const [translatedContent, setTranslatedContent] = useState("");
  const [translationStream, setTranslationStream] = useState("");
  const [originalContent, setOriginalContent] = useState(null);

  const [initialContent, setInitialContent] = useState("");
  const [metafields, setMetafields] = useState([]);

  // Preview state
  const [viewMode, setViewMode] = useState(null);

  // Edit mode state
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedContent, setEditedContent] = useState("");

  // Load location options for AsyncSelect
  const loadLocationOptions = (inputValue, callback) => {
    if (!inputValue) {
      callback([]);
      return;
    }
    const filteredLocations = allLocations.filter(location =>
      location.label.toLowerCase().includes(inputValue.toLowerCase())
    );
    callback(filteredLocations);
  };

  // Use the streaming content hook
  const {
    isGenerating,
    isStreaming,
    hasFirstChunk,
    isStreamingDone,
    streamingContent,
    finalContent,
    parsedHTMLContent,
    error,
    chunkCount,
    lastChunkTime,
    streamingStartTime,
    generateContent,
    resetContent,
    clearError
  } = useStreamingContent(generateContentWithStreaming);

  // Language options using ISO6391
  const languageOptions = React.useMemo(
    () =>
      ISO6391.getAllNames().map((name) => ({
        value: ISO6391.getCode(name),
        label: name,
      })),
    []
  );

  // Initialize form data from contentData
  useEffect(() => {
    if (contentData) {
      setFormData({
        title: contentData.title || contentData.name || "",
        prompt: contentData.prompt || "",
        language: contentData.language ? {
          label: contentData.language,
          value: contentData.language,
        } : null,
        locationFocus: contentData.locationFocus ? {
          label: contentData.locationFocus,
          value: contentData.locationFocus,
        } : null,
        role: contentData.role || "",
        goal: contentData.goal || "",
        constraints: contentData.constraints || "",
      });

      setUploadedData({
        tags: contentData.tags || "",
        uid: contentData.uid || contentData.id || "",
      });

      setImageUrl(contentData.image || "");
      
      if (contentData.generatedContent || contentData.generated_content) {
        setHasGeneratedContent(true);
        const existingContent = contentData.generated_content || contentData.generatedContent || "";
        if (existingContent) {
          setInitialContent(existingContent);
        }
      }
      if (contentData.metafields && Array.isArray(contentData.metafields)) {
        setMetafields(contentData.metafields);
      }

      if (contentData.id) {
        setSavedContentId(contentData.id);
        setIsContentSaved(true);
      }
    }
  }, [contentData]);

  // Fetch locations from API
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const response = await axiosInstance.get('/api/v1/listing?type=country');
        if (response.data && response.data.data && Array.isArray(response.data.data)) {
          const locationOptions = response.data.data.map(item => ({
            value: item.name,
            label: item.name,
          }));
          setAllLocations(locationOptions);
        }
      } catch (error) {
        console.error("Failed to fetch locations:", error);
        toast.error("Failed to fetch locations");
      }
    };
    fetchLocations();
  }, []);

  // Monitor content generation completion
  useEffect(() => {
    if (isStreamingDone && finalContent) {
      setHasGeneratedContent(true);
    }
  }, [isStreamingDone, finalContent]);

  // Monitor form changes after saving
  useEffect(() => {
    if (isContentSaved) {
      setHasUnsavedChanges(true);
    }
  }, [formData, imageUrl, isContentSaved]);

  // Warn user before leaving with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges, isContentSaved]);

  // Image upload functions
  const validateImage = (file) => {
    const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp"];
    const maxSize = 10 * 1024 * 1024; // 10MB

    if (!allowedTypes.includes(file.type)) {
      setImageError("Please select a valid image file (JPEG, PNG, GIF, or WebP)");
      return false;
    }

    if (file.size > maxSize) {
      setImageError("Image size should not exceed 10MB");
      return false;
    }

    setImageError("");
    return true;
  };

  const handleImageSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!validateImage(file)) return;

    setSelectedImage(file);
    setImageError("");

    // Upload the image immediately
    await uploadImage(file);
  };

  const uploadImage = async (file) => {
    if (!file) return;

    setIsUploadingImage(true);
    setImageError("");

    try {
      const formData = new FormData();
      formData.append("file[]", file);
      
      // Get the current company ID dynamically
      const currentCompanyId = await getCurrentCompanyId();
      if (!currentCompanyId) {
        setImageError("Could not determine company ID. Please try again.");
        return;
      }
      
      const jsonPayload = {
        request_type: "blog",
        additional_data: {
          documents: [currentCompanyId],
          uid: Date.now().toString() + "-" + Math.random().toString(36).substring(2, 6),
        },
      };
      formData.append("request_json", JSON.stringify(jsonPayload));

      const response = await uploadCompanyKnowledgeFiles(formData);
      
      if (response.files && response.files.length > 0) {
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        const permanentUrl = await getPermanentImageUrlFromKnowledgeBase(currentCompanyId, file.name);
        
        if (permanentUrl) {
          setImageUrl(permanentUrl);
        } else {
          setImageUrl(response.files[0]);
        }
      }
    } catch (error) {
      console.error('Image upload error:', error);
      setImageError(`Upload failed: ${error.message || 'Unknown error occurred'}`);
      toast.error(`Image upload failed: ${error.message || 'Unknown error occurred'}`);
    } finally {
      setIsUploadingImage(false);
    }
  };

  const getCurrentCompanyId = async () => {
    try {
      // This is a placeholder - implement based on your company context
      // You might get this from localStorage, context, or API call
      return localStorage.getItem('companyId') || 'default-company-id';
    } catch (error) {
      console.error('Error getting company ID:', error);
      return null;
    }
  };

  const getPermanentImageUrlFromKnowledgeBase = async (companyId, fileName) => {
    try {
      // This is a placeholder - implement based on your knowledge base API
      // You might make an API call to get the permanent URL
      return null;
    } catch (error) {
      console.error('Error getting permanent image URL:', error);
      return null;
    }
  };

  const removeImage = () => {
    setSelectedImage(null);
    setImageUrl("");
    setImageError("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleReplaceImage = () => {
    // Small delay to ensure the file input is ready
    setTimeout(() => {
      fileInputRef.current?.click();
    }, 100);
  };

  const handlePreview = async (mode) => {
    const currentId = savedContentId;
    if (!currentId) {
      toast.warning("Please save the content as draft before previewing.");
      return;
    }

    // Get the current content - prioritize translatedContent (newest) > initialContent (edited) > other
    let currentContent = "";
    if (translatedContent && translatedContent.trim()) {
      currentContent = translatedContent;
    } else if (initialContent && initialContent.trim()) {
      currentContent = initialContent;
    } else if (finalContent) {
      currentContent = finalContent;
    } else if (parsedHTMLContent) {
      currentContent = parsedHTMLContent;
    } else if (streamingContent) {
      currentContent = streamingContent;
    }


    // Prepare content data for preview - use original contentData as base and update with current form data
    const previewData = {
      ...contentData, // Start with all original data
      id: currentId,
      name: formData.title || contentData?.name,
      title: formData.title || contentData?.title,
      prompt: formData.prompt || contentData?.prompt,
      language: formData.language?.label || contentData?.language || "English",
      locationFocus: formData.locationFocus?.label || contentData?.locationFocus || contentData?.location_focus || "India",
      role: formData.role || contentData?.role || "",
      goal: formData.goal || contentData?.goal || "",
      constraints: formData.constraints || contentData?.constraints || "",
      generatedContent: currentContent || contentData?.generatedContent || contentData?.generated_content || "",
      generated_content: currentContent || contentData?.generatedContent || contentData?.generated_content || "",
      image: imageUrl || contentData?.image || "",
      status: isContentSaved ? "draft" : "unsaved",
      created_at: contentData?.created_at || new Date().toISOString(),
      metafields: metafields || contentData?.metafields || [],
      tags: uploadedData?.tags || contentData?.tags || "",
      uid: uploadedData?.uid || contentData?.uid || contentData?.id || ""
    };


    try {
      navigate(`/skills/free-form-content/preview/${currentId}`, { 
        state: { 
          contentData: previewData,
          viewMode: mode || viewMode,
          source: 'edit' 
        } 
      });
    } catch (err) {
      toast.error("Failed to open preview.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate mandatory fields
    if (!formData.language || !formData.language.value) {
      toast.error("Please select a language. Language is required.");
      return;
    }
    
    if (!formData.locationFocus || !formData.locationFocus.value) {
      toast.error("Please select a location focus. Location is required.");
      return;
    }
    
    // Create comprehensive prompt by appending role, goal, and constraints
    let comprehensivePrompt = formData.prompt;
    
    if (formData.role || formData.goal || formData.constraints) {
      const additionalContext = [];
      if (formData.role) additionalContext.push(`Role: ${formData.role}`);
      if (formData.goal) additionalContext.push(`Goal: ${formData.goal}`);
      if (formData.constraints) additionalContext.push(`Constraints: ${formData.constraints}`);
      
      if (additionalContext.length > 0) {
        comprehensivePrompt = `${formData.prompt}\n\nAdditional Context:\n${additionalContext.join('\n')}`;
      }
    }
    
    // Prepare form data
    const requestData = {
      title: formData.title,
      prompt: comprehensivePrompt,
      language: formData.language?.value || formData.language,
      locationFocus: formData.locationFocus?.value || formData.locationFocus,
      role: formData.role,
      goal: formData.goal,
      constraints: formData.constraints,
      tags: uploadedData.tags,
      uid: uploadedData.uid,
      image: imageUrl,
    };

    // Reset content state when starting new generation
    setHasGeneratedContent(false);
    setIsContentSaved(false);
    setSavedContentId(null);
    setHasUnsavedChanges(false);
    
    setInitialContent("");
    setMetafields([]);

    setTranslatedContent("");
    setTranslationStream("");
    setOriginalContent("");
    setIsTranslating(false);
    
    // Reset edit mode state
    setEditedContent("");
    setIsEditMode(false);
    
    // Use the streaming hook to generate content
    await generateContent(requestData);
  };

  const handleBack = () => {
    navigate('/skills/free-form-content');
  };

  const handleSaveAsDraft = () => {
    // Always show modal for title input (same as product page)
    setShowDraftModal(true);
  };

  const saveDraft = async (overrideTitle = null, forceNewRecord = false) => {
    try {
      // Create comprehensive prompt by appending role, goal, and constraints
      let comprehensivePrompt = formData.prompt;
      
      if (formData.role || formData.goal || formData.constraints) {
        const additionalContext = [];
        if (formData.role) additionalContext.push(`Role: ${formData.role}`);
        if (formData.goal) additionalContext.push(`Goal: ${formData.goal}`);
        if (formData.constraints) additionalContext.push(`Constraints: ${formData.constraints}`);
        
        if (additionalContext.length > 0) {
          comprehensivePrompt = `${formData.prompt}\n\nAdditional Context:\n${additionalContext.join('\n')}`;
        }
      }

      // Get the current content - prioritize the most complete content available
      let currentContent = "";
      if (initialContent && initialContent.trim()) {
        currentContent = initialContent;
      } else if (translatedContent && translatedContent.trim()) {
         currentContent = translatedContent;
      } else if (finalContent) {
        currentContent = finalContent;
      } else if (parsedHTMLContent) {
        currentContent = parsedHTMLContent;
      } else if (streamingContent) {
        currentContent = streamingContent;
      }

      const draftData = {
        title: overrideTitle || formData.title,
        prompt: comprehensivePrompt,
        language: formData.language?.value || formData.language,
        locationFocus: formData.locationFocus?.value || formData.locationFocus,
        role: formData.role,
        goal: formData.goal,
        constraints: formData.constraints,
        tags: uploadedData.tags,
        uid: uploadedData.uid,
        image: imageUrl,
        generatedContent: currentContent
      };

      let response;
      
      // Use PATCH for updates, POST for new records
      if (savedContentId && !forceNewRecord) {
        // Update existing draft using PATCH
        response = await updateFreeFormContentDraft(savedContentId, draftData);
      } else {
        // Create new draft using POST
        response = await saveFreeFormContentDraft(draftData);
      }
      
      if (response && response.success) {
        setSavedContentId(response.data?.id || null);
        setIsContentSaved(true);
        setHasUnsavedChanges(false);
        const title = overrideTitle || formData.title || 'Untitled Content';
        const action = (savedContentId && !forceNewRecord) ? 'updated' : 'saved';
        toast.success(`Draft "${title}" ${action} successfully!`);
      } else {
        console.warn('Unexpected response structure:', response);
        toast.warning('Draft saved but no ID returned. Share functionality may not work.');
      }
    } catch (error) {
      console.error('Error saving draft:', error);
      toast.error(`Failed to save draft: ${error.message}`);
    }
  };

  const handleDraftConfirm = async (draftChoice, newDraftTitle) => {
    if (draftChoice === "update") {
      // Update existing draft
      await saveDraft(null, false);
    } else if (draftChoice === "new") {
      // Create new draft
      console.log("Creating new freeform draft with title:", newDraftTitle);
      
      // Update the form title and clear the saved content ID to force creation of new record
      setFormData(prev => ({ ...prev, title: newDraftTitle }));
      setSavedContentId(null);
      
      // Save with new title and force new record
      await saveDraft(newDraftTitle, true);
    }
    
    // Close the modal
    setShowDraftModal(false);
  };

  const handleShare = () => {
    if (!savedContentId) {
      toast.warning('Please save the content first before sharing.');
      return;
    }

    // Create share URL
    const shareUrl = `${window.location.origin}/skills/free-form-content/${savedContentId}`;
    
    // Copy to clipboard
    navigator.clipboard.writeText(shareUrl)
      .then(() => {
        toast.success('Link copied to clipboard!');
      })
      .catch(() => {
        toast.error('Failed to copy link. Please copy manually: ' + shareUrl);
      });
  };

  const handleReset = () => {
    // Reset content state
    resetContent();
    setHasGeneratedContent(false);
    setIsContentSaved(false);
    setSavedContentId(null);
    setHasUnsavedChanges(false);
    
    // Reset edit mode state
    setInitialContent("");
    setEditedContent("");
    setIsEditMode(false);
    setMetafields([]);
    
    // Reset form data to original contentData
    if (contentData) {
      setFormData({
        title: contentData.title || contentData.name || "",
        prompt: contentData.prompt || "",
        language: contentData.language ? {
          label: contentData.language,
          value: contentData.language,
        } : null,
        locationFocus: contentData.locationFocus ? {
          label: contentData.locationFocus,
          value: contentData.locationFocus,
        } : null,
        role: contentData.role || "",
        goal: contentData.goal || "",
        constraints: contentData.constraints || "",
      });

      setUploadedData({
        tags: contentData.tags || "",
        uid: contentData.uid || contentData.id || "",
      });

      setImageUrl(contentData.image || "");
      
      // Restore initial content if it exists
      if (contentData.generatedContent || contentData.generated_content) {
        const existingContent = contentData.generated_content || contentData.generatedContent || "";
        if (existingContent) {
          setInitialContent(existingContent);
          setHasGeneratedContent(true);
        }
      }

      // Restore metafields if they exist
      if (contentData.metafields && Array.isArray(contentData.metafields)) {
        setMetafields(contentData.metafields);
      }
    }
    
    // Clear file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleDiscardChanges = () => {
    if (window.confirm('Are you sure you want to discard your unsaved changes? This action cannot be undone.')) {
      // Reset to the last saved state
      setHasUnsavedChanges(false);
      handleReset();
    }
  };

  // Helper function to check if any content exists
  const hasAnyContent = () => {
    return !!(
      (initialContent && initialContent.trim()) ||
      (finalContent && finalContent.trim()) ||
      (parsedHTMLContent && parsedHTMLContent.trim()) ||
      (streamingContent && streamingContent.trim()) ||
      (translatedContent && translatedContent.trim()) ||
      hasGeneratedContent ||
      (contentData && (contentData.generatedContent || contentData.generated_content))
    );
  };

  // Helper function to extract HTML content from JSON if needed
  const extractHtmlFromContent = (content) => {
    if (!content || typeof content !== 'string') return content;
    const trimmed = content.trim();
    if (trimmed.startsWith('{')) {
      try {
        const parsed = JSON.parse(trimmed);
        if (parsed.html_content) return parsed.html_content;
        if (parsed.content) return parsed.content;
        if (parsed.content_type || parsed.metadata) return "";
      } catch (e) {
        const htmlMatch = trimmed.match(/"html_content"\s*:\s*"([\s\S]*?)"\s*(,|\})/);
        if (htmlMatch && htmlMatch[1]) {
          return htmlMatch[1].replace(/\\"/g, '"').replace(/\\n/g, '\n').replace(/\\/g, '');
        }
        if (trimmed.includes('content_type') || trimmed.includes('metadata')) return "";
      }
    }
    if (content.includes('content_type') && !content.includes('html_content') && !content.includes('<')) {
      return "";
    }
    return content;
  };

  const htmlToPlainText = (html) => {
    if (!html || typeof html !== 'string') return html;
    
    // If it doesn't contain HTML tags, return as-is
    if (!/<[^>]+>/.test(html)) {
      return html;
    }
    if (html.match(/<[^>]+>/g)?.every(tag => /^<br\s*\/?>$/i.test(tag))) {
      // Only contains <br> tags, safe to convert
      return html.replace(/<br\s*\/?>/gi, '\n');
    }
    
    return html;
  };

  // Handle edit mode toggle
  const handleToggleEditMode = () => {
    if (!isEditMode) {
      // Use initialContent as the single source of truth (it contains the latest content - translated or edited)
      let currentContent = initialContent && initialContent.trim() 
        ? initialContent 
        : (translatedContent && translatedContent.trim()
            ? translatedContent
            : (finalContent || parsedHTMLContent || streamingContent || ""));
      const cleanedContent = extractHtmlFromContent(currentContent);
      const plainTextContent = htmlToPlainText(cleanedContent);
      setEditedContent(plainTextContent || "");
    }
    setIsEditMode(!isEditMode);
  };

   const preserveWhitespace = (content) => {
    if (!content || typeof content !== 'string') return content;
    if (/<[^>]+>/.test(content)) {
      return content;
    }
     return content.replace(/\n/g, '<br>');
  };

  // Handle saving edited content
  const handleSaveEditedContent = () => {
    const cleanedContent = extractHtmlFromContent(editedContent);
     const preservedContent = preserveWhitespace(cleanedContent);
    
    // Update initialContent as the single source of truth
    // If translatedContent exists, update it too to keep them in sync
    setInitialContent(preservedContent);
    if (translatedContent && translatedContent.trim()) {
      // Keep translatedContent in sync with initialContent
      setTranslatedContent(preservedContent);
    }
    if (cleanedContent.trim()) {
      setHasGeneratedContent(true);
      setHasUnsavedChanges(true);
      toast.success("Content saved successfully!");
    } else {
      setHasGeneratedContent(false);
      // Clear both if content is empty
      setTranslatedContent("");
      toast.info("Editor cleared. You can add content manually.");
    }
    setIsEditMode(false);
  };

  // Handle canceling edit
  const handleCancelEdit = () => {
    // Use initialContent as the single source of truth (it contains the latest content)
    let currentContent = initialContent && initialContent.trim() 
      ? initialContent 
      : (translatedContent && translatedContent.trim()
          ? translatedContent
          : (finalContent || parsedHTMLContent || streamingContent || ""));
    setEditedContent(currentContent || "");
    setIsEditMode(false);
  };

  // Translation function using the service
  const onTranslate = async () => {

    function cleanArray(arr) {
      return arr.map(item => cleanObject(item));
    }
    
    function cleanObject(obj) {
      if (obj === null || obj === undefined || obj === 'none' || obj === 'None') {
        return "";
      }
      
      if (Array.isArray(obj)) {
        return obj.map(item => cleanObject(item));
      }
      
      if (typeof obj === 'object' && obj !== null) {
        const cleaned = {};
        for (const key in obj) {
          if (obj.hasOwnProperty(key)) {
            const value = obj[key];
            
            if (typeof value === 'string') {
              if ((value.startsWith('[') && value.endsWith(']')) || 
                  (value.startsWith('{') && value.endsWith('}'))) {
                try {
                  const parsed = JSON.parse(value);
                  cleaned[key] = cleanObject(parsed);
                } catch (e) {
                  cleaned[key] = value;
                }
              } else {
                cleaned[key] = value;
              }
            } else {
              cleaned[key] = cleanObject(value);
            }
          }
        }
        return cleaned;
      }
      
      return obj;
    }

    if (!finalContent && !streamingContent && !initialContent && (!metafields || metafields.length === 0)) {
      toast.error("Please generate content first before translating");
      return;
    }

    // Check if language is selected
    if (!formData.language) {
      toast.error("Please select a target language for translation");
      return;
    }

    setIsTranslating(true);
    setTranslatedContent("");
    setTranslationStream("");
    
    // Store the original content before translation
    if (!originalContent) {
      setOriginalContent({
        finalContent: finalContent,
        streamingContent: streamingContent,
        parsedHTMLContent: parsedHTMLContent,
        initialContent: initialContent,
        formData: { ...formData }
      });
    }
    
    try {
       const contentToTranslate = initialContent && initialContent.trim() 
        ? initialContent 
        : (parsedHTMLContent || finalContent || streamingContent || "");

      const metafieldsToTranslate = JSON.parse(JSON.stringify(metafields));
      const cleanedData = cleanArray(metafieldsToTranslate);
      

      // Prepare the translation request data
      const translationData = {
        content: contentToTranslate,
        metafields: cleanedData || [], // Include metafields in translation
        source_language: "English",
        target_language: formData.language?.label || formData.language || "English",
        target_region: formData.locationFocus?.label || formData.locationFocus || "Global"
      };

      function transformValue(data) {
        // Check if it's a string
        if (typeof data === 'string') {
          // Replace Python booleans in the string
          const fixed = data
            .replace(/:\s*True\b/g, ': true')
            .replace(/:\s*False\b/g, ': false')
            .replace(/,\s*True\b/g, ', true')
            .replace(/,\s*False\b/g, ', false');
          
          // Now parse the corrected JSON string
          return JSON.parse(fixed);
        }
        
        // If it's already an object, use the recursive approach
        return convertBooleans(data);
      }
      
      function convertBooleans(obj) {
        if (obj === null || typeof obj !== 'object') {
          return obj;
        }
        
        if (Array.isArray(obj)) {
          return obj.map(item => convertBooleans(item));
        }
        
        const result = {};
        for (const key in obj) {
          const value = obj[key];
          
          if (value === 'True') {
            result[key] = true;
          } else if (value === 'False') {
            result[key] = false;
          } else if (typeof value === 'object' && value !== null) {
            result[key] = convertBooleans(value);
          } else {
            result[key] = value;
          }
        }
        
        return result;
      }

      // Prepare JSON data in the format expected by the translation service
      // The API returns the same format as input, just translated
      const jsonData = {
        content: contentToTranslate,
        metafields: cleanedData || []
      };

      // Use the same translation service as blog and product
      await translateContent({
        jsonData,
        sourceLanguage: "English",
        targetLanguage: formData.language?.label || formData.language || "English",
        targetRegion: formData.locationFocus?.label || formData.locationFocus || "Global",
        sessionId: null, // Free form doesn't use session ID
        onChunk: (chunk, accumulatedData) => {
          // Update translation stream for UI feedback
          setTranslationStream(accumulatedData);
          
          // If chunk is a parsed object, try to update content incrementally
          if (typeof chunk === 'object' && chunk !== null) {
            if (chunk.content) {
              setTranslatedContent(chunk.content);
            }
            if (chunk.metafields && Array.isArray(chunk.metafields)) {
              setMetafields(chunk.metafields);
            }
          }
        },
        onComplete: (finalData) => {
          try {
            // Parse the final translated data
            let parsedTranslation = finalData;
            
            // If it's a string, try to parse it
            if (typeof finalData === 'string') {
              try {
                parsedTranslation = JSON.parse(finalData);
              } catch (e) {
                // If parsing fails, might be HTML content directly
                parsedTranslation = { content: finalData };
              }
            }
            
            // Handle the translated response - API returns same format as input
            let translatedContentValue = "";
            if (parsedTranslation && parsedTranslation.content) {
              // If content is a string that looks like JSON, try to parse it
              if (typeof parsedTranslation.content === 'string' && !/<[^>]*>/.test(parsedTranslation.content)) {
                try {
                  translatedContentValue = transformValue(parsedTranslation.content);
                } catch (e) {
                  translatedContentValue = parsedTranslation.content;
                }
              } else {
                translatedContentValue = parsedTranslation.content;
              }
            } else if (typeof parsedTranslation === 'string') {
              translatedContentValue = parsedTranslation;
            } else {
              translatedContentValue = parsedTranslation;
            }
            
            // Update initialContent as the single source of truth (this is the current content state)
            setInitialContent(translatedContentValue);
            // Also update translatedContent for display priority
            setTranslatedContent(translatedContentValue);
            setHasGeneratedContent(true);
            setHasUnsavedChanges(true);
            
            // Update metafields with translated versions if available
            if (parsedTranslation?.metafields && Array.isArray(parsedTranslation.metafields) && parsedTranslation.metafields.length > 0) {
              setMetafields(parsedTranslation.metafields);
            }
            
            toast.success("Translation completed successfully! Your content has been replaced with the translated version.");
          } catch (parseError) {
            console.error("Error parsing translation response:", parseError);
            toast.warning("Translation completed but failed to parse response format");
          } finally {
            setIsTranslating(false);
          }
        },
        onError: (error) => {
          console.error("Translation error:", error);
          toast.error("Translation failed: " + (error.message || "Unknown error"));
          setIsTranslating(false);
        }
      });
      
    } catch (error) {
      console.error("Translation error:", error);
      toast.error("Translation failed: " + (error.message || "Unknown error"));
      setIsTranslating(false);
    }
  };


  // Helper function to process complete HTML documents and extract body content with inline styles
  const processCompleteHTMLDocument = (htmlDocument) => {
    try {
      // Create a temporary div to parse the HTML document
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = htmlDocument;
      
      // Extract the body content
      const bodyElement = tempDiv.querySelector('body');
      if (bodyElement) {
        // Get all the content from the body
        let bodyContent = bodyElement.innerHTML;
        
        // Extract CSS styles from the head section
        const headElement = tempDiv.querySelector('head');
        if (headElement) {
          const styleElement = headElement.querySelector('style');
          if (styleElement) {
            const cssText = styleElement.textContent;
            bodyContent = applyCSSToHTML(bodyContent, cssText);
          }
        }
        
        return bodyContent;
      }
      
      // Fallback: return the original content
      return htmlDocument;
    } catch (error) {
      console.error('Error processing complete HTML document:', error);
      return htmlDocument;
    }
  };

  // Helper function to apply CSS styles to HTML content
  const applyCSSToHTML = (htmlContent, cssText) => {
    try {
      // Create a temporary container to work with
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = htmlContent;
      
      // Parse CSS rules
      const cssRules = parseCSS(cssText);
      cssRules.forEach(rule => {
        const elements = tempDiv.querySelectorAll(rule.selector);
        elements.forEach(element => {
          // Apply each CSS property
          Object.keys(rule.properties).forEach(property => {
            element.style[property] = rule.properties[property];
          });
        });
      });
      
      return tempDiv.innerHTML;
    } catch (error) {
      console.error('Error applying CSS to HTML:', error);
      return htmlContent;
    }
  };

  // Helper function to parse CSS text into rules
  const parseCSS = (cssText) => {
    const rules = [];
    const ruleRegex = /([^{]+)\{([^}]+)\}/g;
    let match;
    
    while ((match = ruleRegex.exec(cssText)) !== null) {
      const selector = match[1].trim();
      const propertiesText = match[2];
      const properties = {};
      
      // Parse properties
      const propertyRegex = /([^:]+):\s*([^;]+);?/g;
      let propMatch;
      
      while ((propMatch = propertyRegex.exec(propertiesText)) !== null) {
        const property = propMatch[1].trim();
        const value = propMatch[2].trim();
        properties[property] = value;
      }
      
      rules.push({ selector, properties });
    }
    
    return rules;
  };

  // Helper function to wrap translated text in original HTML structure
  const wrapTextInHTMLStructure = (translatedText, originalHTML) => {
    try {
      // Create a temporary DOM element to parse the original HTML
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = originalHTML;
      
      // Find all text nodes and replace them with translated text
      const walker = document.createTreeWalker(
        tempDiv,
        NodeFilter.SHOW_TEXT,
        null,
        false
      );
      
      let textNodes = [];
      let node;
      while (node = walker.nextNode()) {
        if (node.textContent.trim()) {
          textNodes.push(node);
        }
      }
      
      // If we have text nodes, replace them with translated content
      if (textNodes.length > 0) {
        // For now, just replace the first text node with the translated content
        // This is a simplified approach - in a real implementation, you'd want to
        // map the translated text more intelligently to different sections
        textNodes[0].textContent = translatedText;
        return tempDiv.innerHTML;
      }
      
      // Fallback: wrap the translated text in a div with the same styling
      const outerDiv = tempDiv.querySelector('div');
      if (outerDiv) {
        const style = outerDiv.getAttribute('style') || '';
        return `<div style="${style}">${translatedText}</div>`;
      }
      
      // Last fallback: return the translated text as is
      return translatedText;
    } catch (error) {
      console.error('Error wrapping text in HTML structure:', error);
      return translatedText;
    }
  };

  // Helper function to fix malformed HTML with missing spaces in attributes
  const fixMalformedHTML = (html) => {
    try {
      let fixed = html;
      
      // First, decode any escaped characters
      fixed = fixed.replace(/\\n/g, '\n').replace(/\\"/g, '"').replace(/\\/g, '');
      
      // Fix specific malformed patterns with precise regex
      // Fix <divstyle=" -> <div style="
      fixed = fixed.replace(/<divstyle="/g, '<div style="');
      
      // Fix <h1style=" -> <h1 style="
      fixed = fixed.replace(/<h1style="/g, '<h1 style="');
      
      // Fix <h2style=" -> <h2 style="
      fixed = fixed.replace(/<h2style="/g, '<h2 style="');
      
      // Fix <pstyle=" -> <p style="
      fixed = fixed.replace(/<pstyle="/g, '<p style="');
      
      // Fix any other tag+style combinations
      fixed = fixed.replace(/<([a-zA-Z]+)style="/g, '<$1 style="');
      
      // Fix missing spaces between CSS properties in style attributes
      fixed = fixed.replace(/style="([^"]*)"/g, (match, styleContent) => {
        // Add spaces before CSS properties that are missing them
        const fixedStyle = styleContent
          .replace(/([a-zA-Z-]+):/g, ' $1:') // Add space before CSS property names
          .replace(/([a-zA-Z-]+):\s*([^;]+);/g, '$1: $2;') // Ensure space after colon
          .replace(/\s+/g, ' ') // Normalize multiple spaces to single space
          .trim();
        return `style="${fixedStyle}"`;
      });
      
      // Fix missing spaces in HTML attributes (like max-width, font-size, etc.)
      fixed = fixed.replace(/([a-zA-Z-]+)-([a-zA-Z-]+):/g, '$1-$2:');
      
      // Fix missing spaces around colons in CSS values
      fixed = fixed.replace(/:\s*([^;]+);/g, ': $1;');
      
      // Fix missing spaces around semicolons
      fixed = fixed.replace(/;([^;])/g, '; $1');
      
      return fixed;
    } catch (error) {
      console.warn('Error fixing malformed HTML:', error);
      return html;
    }
  };


  if (!contentData) {
    return (
      <div>
        <Breadcrumb />
        <div className="text-center p-5">
          <h3>No content data found</h3>
          <p>Please go back to the content list and select an item to edit.</p>
          <Button variant="primary" onClick={handleBack}>
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <ToastContainer />
      <Breadcrumb />

      <div className="create-header">
        <h2>Edit Free Form Content</h2>
        <div className="header-actions">
          {hasGeneratedContent && (
            <button 
              className="btn bgprimary" 
              onClick={handleSaveAsDraft}
              disabled={isGenerating}
            >
              Save as draft
            </button>
          )}
          
          {/* Show Discard Changes button when there are unsaved changes */}
          {hasUnsavedChanges && isContentSaved && (
            <Button 
              variant="outline-warning" 
              size="sm"
              className="me-2"
              onClick={handleDiscardChanges}
              disabled={isGenerating}
            >
              Discard Changes
            </Button>
          )}

          {/* Show Share button only after content is saved and only for local content */}
          {isContentSaved && savedContentId && !contentData?.is_shopify_page && (
            <Button 
              variant="outline-danger" 
              size="sm"
              className="me-2"
              onClick={handleShare}
            >
              Share
            </Button>
          )}
          
          {/* Edit button - always visible to allow manual content entry, even without generated content */}
          <Button
            variant={isEditMode ? "warning" : "outline-primary"}
            size="sm"
            className="me-2"
            onClick={handleToggleEditMode}
            disabled={isGenerating || isTranslating}
          >
            {isEditMode ? "Cancel Edit" : "Edit Content"}
          </Button>
          
          {/* Reset button - always visible but disabled during generation */}
          <Button 
            variant="outline-secondary" 
            size="sm"
            onClick={handleReset}
            disabled={isGenerating}
          >
            Reset
          </Button>
        </div>
      </div>

      <Row>
        {/* Left: Input Form */}
        <Col md={4}>
          {/* Content configuration form panel */}
          <Card className="create-form-panel">
            <Card.Body>
              {/* Content Status Indicator */}
              <div className="mb-3 content-status-indicator">
                {hasGeneratedContent && (
                  <Alert variant="success" className="py-2 mb-3">
                    <strong>✓ Content Generated</strong>
                    {isContentSaved && (
                      <span className="ms-2">• <strong>✓ Saved as Draft</strong></span>
                    )}
                    {hasUnsavedChanges && isContentSaved && (
                      <span className="ms-2">• <strong>⚠️ Unsaved Changes</strong></span>
                    )}
                  </Alert>
                )}
              </div>
              
              {/* Content Configuration Section */}
              <div className="mb-4">
                <h6 className="section-title mb-3">Provide Context</h6>
                <Form onSubmit={handleSubmit}>
                  <Form.Group className="mb-3">
                    <Form.Label>Title</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter a title for your content..."
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Control
                      as="textarea"
                      rows={6}
                      placeholder="Enter your prompt or question here..."
                      value={formData.prompt}
                      onChange={(e) => setFormData({ ...formData, prompt: e.target.value })}
                      required
                    />
                  </Form.Group>

                  {/* Image Upload Section */}
                  <Form.Group className="mb-3">
                    <Form.Label>Expected Structure Image (Optional)</Form.Label>
                    <div className="image-upload-container">
                      {!imageUrl ? (
                        <div 
                          className="image-upload-area"
                          onClick={() => fileInputRef.current?.click()}
                        >
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            onChange={handleImageSelect}
                            style={{ display: 'none' }}
                          />
                          <Upload size={24} className="upload-icon" />
                          <p className="upload-text">Click to upload image</p>
                          <small className="text-muted">
                            Supported: JPEG, PNG (Max 10MB)
                          </small>
                        </div>
                      ) : (
                        <div className="image-preview-container">
                          <img 
                            src={imageUrl} 
                            alt="Content preview" 
                            className="image-preview"
                          />
                          <div className="image-actions">
                            <button
                              type="button"
                              className="replace-image-btn"
                              onClick={handleReplaceImage}
                              title="Replace image"
                            >
                              <Upload size={14} />
                            </button>
                            <button
                              type="button"
                              className="remove-image-btn"
                              onClick={removeImage}
                              title="Remove image"
                            >
                              <X size={14} />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                    {imageError && (
                      <Alert variant="danger" className="mt-2 py-2">
                        {imageError}
                      </Alert>
                    )}
                    {isUploadingImage && (
                      <div className="text-center mt-2">
                        <div className="spinner-border spinner-border-sm" role="status">
                          <span className="visually-hidden">Uploading...</span>
                        </div>
                        <span className="ms-2">Uploading image...</span>
                      </div>
                    )}
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Role</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      placeholder="Enter role"
                      value={formData.role || ""}
                      onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Goal</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      placeholder="Enter goal"
                      value={formData.goal || ""}
                      onChange={(e) => setFormData({ ...formData, goal: e.target.value })}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Constraints</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      placeholder="Enter constraints"
                      value={formData.constraints || ""}
                      onChange={(e) => setFormData({ ...formData, constraints: e.target.value })}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Language <span className="text-danger">*</span> <span className="text-muted">(Required)</span></Form.Label>
                    <Select
                      options={languageOptions}
                      value={formData.language}
                      onChange={(selected) => setFormData({ ...formData, language: selected })}
                      isClearable
                      isSearchable
                      placeholder="Type to search language..."
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Location Focus <span className="text-danger">*</span> <span className="text-muted">(Required)</span></Form.Label>
                    <AsyncSelect
                      cacheOptions
                      loadOptions={loadLocationOptions}
                      value={formData.locationFocus}
                      onChange={(selected) => setFormData({ ...formData, locationFocus: selected })}
                      isClearable
                      isSearchable
                      placeholder="Type to search location..."
                      noOptionsMessage={({ inputValue }) =>
                        !inputValue
                          ? "Type to search..."
                          : "No locations found for that query"
                      }
                      loadingMessage={() => "Searching locations..."}
                    />
                  </Form.Group>

                  <div className="form-actions">
                    <Button 
                      variant="outline-danger" 
                      onClick={handleBack}
                      className="cancel-btn"
                      type="button"
                    >
                      Cancel
                    </Button>
                    <Button 
                      type="submit" 
                      className="generate-btn"
                      variant="danger"
                      disabled={isGenerating}
                    >
                      {isGenerating ? "Generating..." : "Generate"}
                    </Button>
                  </div>
                  
                  {/* Translate button - styled like blog implementation */}
                  <button
                    type="button"
                    className="optimize-button mt-2"
                    onClick={onTranslate}
                    disabled={isTranslating || (!finalContent && !streamingContent && !initialContent && (!metafields || metafields.length === 0)) || !formData.language}
                  >
                    {isTranslating ? 'Translating...' : 'Translate'}
                  </button>
                  
                  {/* Translation help text */}
                  <div className="mt-2 text-muted" style={{ fontSize: '12px' }}>
                    {!formData.language && (finalContent || streamingContent || initialContent || (metafields && metafields.length > 0)) && (
                      <span>Select a target language to enable translation</span>
                    )}
                    {formData.language && (!finalContent && !streamingContent && !initialContent && (!metafields || metafields.length === 0)) && (
                      <span>Generate content first, then translate</span>
                    )}
                    {formData.language && (finalContent || streamingContent || initialContent || (metafields && metafields.length > 0)) && (
                      <span>Ready to translate to {formData.language.label || formData.language}</span>
                    )}
                  </div>
                  
                  {/* Help text based on current state */}
                  <div className="mt-3 help-text">
                    {!hasGeneratedContent && (
                      <div className="text-muted">
                        Fill in the form and click Generate to create your content.
                      </div>
                    )}
                    {hasGeneratedContent && !isContentSaved && (
                      <div className="text-success">
                        Content generated! Click "Save as Draft" to save your work.
                      </div>
                    )}
                    {isContentSaved && !hasUnsavedChanges && (
                      <div className="text-success">
                        Content saved! You can now share it with others.
                      </div>
                    )}
                    {isContentSaved && hasUnsavedChanges && (
                      <div className="text-warning">
                        You have unsaved changes. Click "Update Draft" to save your modifications.
                      </div>
                    )}
                  </div>
                </Form>
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Right: Streaming and Rendered Output */}
        <Col md={8} className="border-start">
          {/* Preview/Tab Buttons */}
          {!isEditMode && (
            <div className="d-flex gap-4 align-items-center mb-3">
              <h5 style={{ fontWeight: "500", fontSize: "16px" }}>
                Generated Content
              </h5>
              { (
                <div className="device-icons d-flex border border-black rounded justify-content-between align-items-center">
                  <h6 className="mt-2 mx-1">Preview</h6>
                  <div className="d-flex justify-content-center align-items-center">
                    <button 
                      className={`btn ${viewMode === "desktop" ? "active" : ""}`}
                      onClick={() => {
                        setViewMode("desktop");
                        handlePreview("desktop");
                      }}
                      style={{
                        border: viewMode === "desktop" ? "1px solid #E36250" : "1px solid #ccc",
                        backgroundColor: viewMode === "desktop" ? "#E36250" : "transparent",
                        color: viewMode === "desktop" ? "white" : "#333",
                        padding: "4px 8px",
                        margin: "0 2px",
                        borderRadius: "4px"
                      }}
                    >
                      <BiLaptop size={16} />
                    </button>
                    <span style={{ margin: "0 4px" }}>|</span>
                    <button 
                      className={`btn ${viewMode === "mobile" ? "active" : ""}`}
                      onClick={() => {
                        setViewMode("mobile");
                        handlePreview("mobile");
                      }}
                      style={{
                        border: viewMode === "mobile" ? "1px solid #E36250" : "1px solid #ccc",
                        backgroundColor: viewMode === "mobile" ? "#E36250" : "transparent",
                        color: viewMode === "mobile" ? "white" : "#333",
                        padding: "4px 8px",
                        margin: "0 2px",
                        borderRadius: "4px"
                      }}
                    >
                      <BiMobile size={16} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Edit Mode - Show HTML Editor */}
          {isEditMode ? (
            <Card className="create-form-panel">
              <Card.Body>
                <div className="mb-3">
                  <h6 className="mb-2">Edit HTML Content</h6>
                  <p className="text-muted small mb-3">
                    You can add content manually or edit existing content. Type your HTML content in the editor below and click "Save Changes" to apply. You can start with an empty editor and add content from scratch.
                  </p>
                  <Form.Control
                    as="textarea"
                    rows={20}
                    value={editedContent}
                    onChange={(e) => setEditedContent(e.target.value)}
                    style={{ 
                      fontFamily: "monospace", 
                      fontSize: "13px",
                      whiteSpace: "pre-wrap",
                      wordWrap: "break-word"
                    }}
                    placeholder="Enter your HTML content here. You can add content manually even if nothing has been generated yet..."
                  />
                </div>
                <div className="d-flex gap-2">
                  <Button
                    variant="success"
                    onClick={handleSaveEditedContent}
                    title="Save your content (can be empty to start fresh)"
                  >
                    Save Changes
                  </Button>
                  <Button
                    variant="outline-secondary"
                    onClick={handleCancelEdit}
                  >
                    Cancel
                  </Button>
                </div>
              </Card.Body>
            </Card>
          ) : (
            <StreamingContentRenderer
              title={formData.title ? formData.title : "Generated Content"}
              isStreaming={isStreaming || isTranslating}
              isGenerating={isGenerating}
              isTranslating={isTranslating}
              hasFirstChunk={hasFirstChunk || (isTranslating && translationStream.length > 0)}
              isStreamingDone={isStreamingDone && !isTranslating}
              streamingContent={isTranslating ? translationStream : streamingContent}
              parsedHTMLContent={
                isTranslating 
                  ? (initialContent && initialContent.trim() ? initialContent : parsedHTMLContent)
                  : (translatedContent && translatedContent.trim()
                      ? translatedContent
                      : (initialContent && initialContent.trim() 
                          ? initialContent 
                          : parsedHTMLContent))
              }
              finalContent={
                isTranslating
                  ? (initialContent && initialContent.trim() ? initialContent : finalContent)
                  : (translatedContent && translatedContent.trim()
                      ? translatedContent
                      : (initialContent && initialContent.trim()
                          ? initialContent
                          : finalContent))
              }
              initialContent={initialContent}
              noContentMessage="No content generated yet. Fill in the form and click Generate to see results."
              metafields={metafields || []}
              chunkCount={chunkCount}
              lastChunkTime={lastChunkTime}
              streamingStartTime={streamingStartTime}
            />
          )}
          
        </Col>
      </Row>
      <LocalDraftModal
        show={showDraftModal}
        onHide={() => setShowDraftModal(false)}
        onConfirm={handleDraftConfirm}
        isChecking={isGenerating}
      />
    </div>
  );
};

export default EditFreeFormContent;
